import { useLoading } from '@genial/components';
import { ErrorListResponse, instanceOfErrorListResponse } from '@genial/services';
import { useState } from 'react';
import { useRecoilValue, useResetRecoilState } from 'recoil';

import { useToastCreator } from '../../../hooks/useToastCreator';
import { useBatchTicketActions } from '../../../store/batchTicket/actions';
import {
  basketAdvisorOrderIdState,
  customerCartState,
  customerCartStatusState,
  productsCartStatusState,
  tagsState,
} from '../../../store/batchTicket/atom';
import { customerCartCountSelector } from '../../../store/batchTicket/selectors';
import { getCustomerBalance } from '../services';
import { errorBalanceUpdateMessage, successBalanceUpdateMessage } from '../utils';
import { CustomerItemDataProps } from '../utils/types';

interface useCustomerItemProps {
  customerItem: CustomerItemDataProps;
}

export const useCustomerItem = ({ customerItem }: useCustomerItemProps) => {
  const customerCartList = useRecoilValue(customerCartState);
  const basketAdvisorOrderId = useRecoilValue(basketAdvisorOrderIdState);
  const customerCartCount = useRecoilValue(customerCartCountSelector);
  const tags = useRecoilValue(tagsState);

  const customerTags = customerItem.tags || [];

  const customerTagIds = new Set(customerTags.map((tag) => tag.tagId));

  if (tags.added[customerItem.customerId]) {
    const newTags = tags.added[customerItem.customerId].filter(
      (tag) => !customerTagIds.has(tag.tagId)
    );
    customerTags.push(...newTags);
    newTags.forEach((tag) => customerTagIds.add(tag.tagId));
  }

  const removedTagIds = new Set(
    tags.removed?.[customerItem.customerId]?.map((tag) => tag.tagId) || []
  );

  const customerTagList = tags.list.filter(
    (tag) => customerTagIds.has(tag.tagId) && !removedTagIds.has(tag.tagId)
  );

  const { hide, show } = useLoading();

  const { showToast, showToastFromError } = useToastCreator('toast-balance-customer-cart-update');

  const { addCustomerToCart, removeCustomerToCart } = useBatchTicketActions();

  const customerStartIsSelected = customerCartList.some(
    (item) => item.accountNumber === customerItem.accountNumber
  );

  const customerCartItem = customerCartList.find(
    (item) => item.accountNumber === customerItem.accountNumber
  );

  const resetCustomerCartStatus = useResetRecoilState(customerCartStatusState);
  const resetProductsCartStatus = useResetRecoilState(productsCartStatusState);

  const [isLoadingBalance, setIsLoadingBalance] = useState(false);
  const [availableBalance, setAvailableBalance] = useState(customerItem.availableBalance);
  const [isLoadingAction, setIsLoadingAction] = useState(false);

  const handleAddCustomerToCart = async () => {
    if (!customerStartIsSelected) {
      if (basketAdvisorOrderId) {
        setIsLoadingAction(true);
      } else {
        show({
          title: 'Aguarde enquanto estamos criando sua cesta. ',
          description: 'Este processo pode levar alguns segundos, não saia desta pagina.',
        });
      }
      await addCustomerToCart({ customerItem, basketAdvisorOrderId });
      hide();
      setIsLoadingAction(false);
    } else {
      setIsLoadingAction(true);
      await removeCustomerToCart(customerCartItem!);
      setIsLoadingAction(false);
    }
  };

  const handleRemoveCustomerToCart = async () => {
    if (customerCartItem) {
      setIsLoadingAction(true);
      await removeCustomerToCart(customerCartItem);
      setIsLoadingAction(false);
      if (customerCartCount === 1) {
        resetCustomerCartStatus();
        resetProductsCartStatus();
      }
    }
  };

  const handleUpdateBalance = async () => {
    try {
      setIsLoadingBalance(true);

      const availableBalanceResponse = await getCustomerBalance(customerItem.accountNumber);

      if (availableBalanceResponse !== availableBalance) {
        setAvailableBalance(availableBalanceResponse);
      }

      showToast(...successBalanceUpdateMessage, { duration: 3000 });
    } catch (error) {
      if (instanceOfErrorListResponse(error as object)) {
        (error as ErrorListResponse).errors?.forEach((errorItem) => showToastFromError(errorItem));
        return;
      }

      showToast(...errorBalanceUpdateMessage);
    } finally {
      setIsLoadingBalance(false);
    }
  };

  return {
    customerTagList,
    customerItemIsSelected: customerStartIsSelected,
    handleAddCustomerToCart,
    handleRemoveCustomerToCart,
    handleUpdateBalance,
    availableBalance,
    isLoadingBalance,
    isLoadingAction,
  };
};
